/**
 * BulkSetAttributeDialog — BulkBar "Set attribute…" dialog (PIM Wave P5 Task
 * 8). A `BottomSheet`: pick a live, non-archived, non-`image` attribute
 * field (`useAllAttributeFields`, filtered — mirrors `AddFieldPicker.tsx`'s
 * `!f.is_archived` precedent), then a typed value input matching the
 * picked field's `field_type` (text/number/boolean/select/size/date — the
 * same 7-1 types `FieldFormDrawer`/`FormPreview` enumerate, `image` excluded
 * per brief), then "Apply to N products" → `useBulkSetAttribute`.
 *
 * Serialization to what the API expects (mirrors
 * `catalog_schema/service.py`'s `_validate_*` per-type rules exactly):
 *   - text   → trimmed string
 *   - number → a JS `number` (not a string — the server's
 *              `_decimal_from_number_like` accepts any `Decimal(str(...))`-
 *              coercible value, but a JS number round-trips as a JSON
 *              number)
 *   - boolean→ a real `boolean`
 *   - select → the picked option's `value_slug`
 *   - size   → a JS `number` + a separate `unit` (the field's
 *              `canonical_unit` pre-selected, changeable within
 *              `unit_kind`'s allowed set)
 *   - date   → an ISO `YYYY-MM-DD` string (native `<input type="date">`)
 * A "clear this attribute instead" checkbox sends `value: null` (server:
 * `_apply_one` treats `value is None` as "remove this field"), bypassing
 * whatever the type-specific input currently holds.
 *
 * On a result with `failed.length > 0`, the dialog stays OPEN (rendering the
 * per-product failure list inline, per brief — "do NOT cram failures into a
 * toast") and the selection is PRESERVED (NOT cleared) — clearing it would
 * make `productIds` (sourced from the caller's selection) collapse to `[]`,
 * breaking the retry affordance ("Apply to 0 products" + an empty
 * `product_ids` array the backend's `min_length=1` rejects). Selection is
 * only cleared, and the dialog only closes, once ALL products succeeded
 * (`failed.length === 0`).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, Field, GPill, Segmented } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import {
  useAllAttributeFields,
  useAttributeFieldOptions,
} from "@/features/admin/attribute-fields/hooks";
import { allowedUnitsForKind, fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import { useBulkSetAttribute } from "@/features/admin/products/hooks";
import type { BulkAttributeValue, BulkSetAttributeFailure } from "@/features/admin/products/api";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)] disabled:opacity-50";

export interface BulkSetAttributeDialogProps {
  open: boolean;
  onClose: () => void;
  productIds: string[];
  /** Called only once ALL products succeeded (`failed.length === 0`) — the
   * caller clears selection. NOT called on a partial failure (selection must
   * survive so the count and a retry stay correct). */
  onApplied: () => void;
}

function initialDialogState() {
  return {
    fieldId: "",
    textValue: "",
    boolValue: "true" as "true" | "false",
    selectValue: "",
    numberValue: "",
    unitValue: "",
    dateValue: "",
    clearValue: false,
  };
}

export function BulkSetAttributeDialog({
  open,
  onClose,
  productIds,
  onApplied,
}: BulkSetAttributeDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  const { data: allFields } = useAllAttributeFields();
  const assignableFields = useMemo(
    () => (allFields ?? []).filter((f) => !f.is_archived && f.field_type !== "image"),
    [allFields],
  );

  const [state, setState] = useState(initialDialogState());
  const [result, setResult] = useState<{ updated: number; failed: BulkSetAttributeFailure[] } | null>(
    null,
  );

  const field: AttributeField | null =
    assignableFields.find((f) => f.id === state.fieldId) ?? null;

  const { data: optionsData } = useAttributeFieldOptions(
    field !== null && field.field_type === "select" ? field.id : null,
  );
  const options = (optionsData?.options ?? []).filter((o) => !o.is_archived);

  const mutation = useBulkSetAttribute();

  useEffect(() => {
    if (open) return;
    setState(initialDialogState());
    setResult(null);
  }, [open]);

  const selectField = (id: string): void => {
    const next = assignableFields.find((f) => f.id === id) ?? null;
    setState({
      ...initialDialogState(),
      fieldId: id,
      unitValue: next !== null && next.field_type === "size" ? next.canonical_unit ?? "" : "",
    });
    setResult(null);
  };

  const valueIsFilled = (): boolean => {
    if (!field) return false;
    switch (field.field_type) {
      case "text":
        return state.textValue.trim() !== "";
      case "number":
        return state.numberValue.trim() !== "";
      case "boolean":
        return true;
      case "select":
        return state.selectValue !== "";
      case "size":
        return state.numberValue.trim() !== "" && state.unitValue !== "";
      case "date":
        return state.dateValue !== "";
      default:
        return false;
    }
  };

  const canSubmit = field !== null && (state.clearValue || valueIsFilled());

  const buildValue = (): { value: BulkAttributeValue; unit: string | null } => {
    if (state.clearValue || !field) return { value: null, unit: null };
    switch (field.field_type) {
      case "text":
        return { value: state.textValue.trim(), unit: null };
      case "number":
        return { value: Number(state.numberValue), unit: null };
      case "boolean":
        return { value: state.boolValue === "true", unit: null };
      case "select":
        return { value: state.selectValue, unit: null };
      case "size":
        return { value: Number(state.numberValue), unit: state.unitValue };
      case "date":
        return { value: state.dateValue, unit: null };
      default:
        return { value: null, unit: null };
    }
  };

  const handleSubmit = (): void => {
    if (!field) return;
    const { value, unit } = buildValue();
    mutation.mutate(
      { ids: productIds, fieldSlug: field.slug, value, unit },
      {
        onSuccess: (res) => {
          setResult({ updated: res.updated.length, failed: res.failed });
          const summary = t("products.bulk.setAttrResult", {
            updated: res.updated.length,
            failed: res.failed.length,
          });
          if (res.failed.length > 0) {
            // Partial failure: keep the selection intact (so the count and a
            // retry stay correct) and keep the dialog open with the inline
            // failure list above.
            toast.error(summary);
          } else {
            onApplied();
            toast.success(summary);
            onClose();
          }
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  const unitOptions =
    field !== null && field.field_type === "size" ? allowedUnitsForKind(field.unit_kind ?? "") : [];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t("products.bulk.setAttrTitle")}
      desktop={isDesktop}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="bulk-attr-field" className="text-[13px] font-semibold text-foreground">
            {t("products.bulk.setAttrFieldLabel")}
          </label>
          <select
            id="bulk-attr-field"
            data-testid="admin-products-bulk-attr-field"
            className={SELECT_CLASS}
            value={state.fieldId}
            onChange={(e) => selectField(e.target.value)}
          >
            <option value="">{t("products.bulk.setAttrFieldPlaceholder")}</option>
            {assignableFields.map((f) => {
              const meta = fieldTypeMeta(f.field_type);
              return (
                <option key={f.id} value={f.id}>
                  {f.name_ru} ({meta ? t(meta.labelKey) : f.field_type})
                </option>
              );
            })}
          </select>
          {assignableFields.length === 0 ? (
            <p className="text-xs text-[var(--g-secondary)]">{t("products.bulk.setAttrNoFields")}</p>
          ) : null}
        </div>

        {field ? (
          <div className="flex flex-col gap-3">
            <label className="flex min-h-[44px] items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="admin-products-bulk-attr-clear"
                checked={state.clearValue}
                onChange={(e) => setState((prev) => ({ ...prev, clearValue: e.target.checked }))}
                className="size-5 accent-[var(--g-primary)]"
              />
              {t("products.bulk.setAttrClear")}
            </label>

            {field.field_type === "text" ? (
              <Field
                label={t("products.bulk.setAttrValueLabel")}
                data-testid="admin-products-bulk-attr-value-text"
                value={state.textValue}
                disabled={state.clearValue}
                onChange={(e) => setState((prev) => ({ ...prev, textValue: e.target.value }))}
              />
            ) : null}

            {field.field_type === "number" ? (
              <Field
                type="number"
                label={t("products.bulk.setAttrValueLabel")}
                data-testid="admin-products-bulk-attr-value-number"
                value={state.numberValue}
                disabled={state.clearValue}
                onChange={(e) => setState((prev) => ({ ...prev, numberValue: e.target.value }))}
              />
            ) : null}

            {field.field_type === "boolean" ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-foreground">
                  {t("products.bulk.setAttrValueLabel")}
                </span>
                <Segmented
                  options={[
                    { value: "true", label: t("pim.preview.widgets.booleanTrue") },
                    { value: "false", label: t("pim.preview.widgets.booleanFalse") },
                  ]}
                  value={state.boolValue}
                  onChange={(next) =>
                    setState((prev) => ({ ...prev, boolValue: next === "false" ? "false" : "true" }))
                  }
                />
              </div>
            ) : null}

            {field.field_type === "select" ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="bulk-attr-select-value" className="text-[13px] font-semibold text-foreground">
                  {t("products.bulk.setAttrValueLabel")}
                </label>
                <select
                  id="bulk-attr-select-value"
                  data-testid="admin-products-bulk-attr-value-select"
                  className={SELECT_CLASS}
                  disabled={state.clearValue}
                  value={state.selectValue}
                  onChange={(e) => setState((prev) => ({ ...prev, selectValue: e.target.value }))}
                >
                  <option value="">{t("pim.preview.widgets.selectNone")}</option>
                  {options.map((o) => (
                    <option key={o.value_slug} value={o.value_slug}>
                      {o.label_ru}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {field.field_type === "size" ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-foreground">
                  {t("products.bulk.setAttrValueLabel")}
                </span>
                <div className="flex gap-2">
                  <Field
                    type="number"
                    className="flex-1"
                    data-testid="admin-products-bulk-attr-value-size"
                    value={state.numberValue}
                    disabled={state.clearValue}
                    onChange={(e) => setState((prev) => ({ ...prev, numberValue: e.target.value }))}
                  />
                  <select
                    aria-label={t("products.bulk.setAttrUnitLabel")}
                    data-testid="admin-products-bulk-attr-unit"
                    className={[SELECT_CLASS, "w-28 shrink-0"].join(" ")}
                    disabled={state.clearValue}
                    value={state.unitValue}
                    onChange={(e) => setState((prev) => ({ ...prev, unitValue: e.target.value }))}
                  >
                    {unitOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {t(`pim.fields.form.unitOption.${unit}`, { defaultValue: unit })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            {field.field_type === "date" ? (
              <Field
                type="date"
                label={t("products.bulk.setAttrValueLabel")}
                data-testid="admin-products-bulk-attr-value-date"
                value={state.dateValue}
                disabled={state.clearValue}
                onChange={(e) => setState((prev) => ({ ...prev, dateValue: e.target.value }))}
              />
            ) : null}
          </div>
        ) : null}

        {result ? (
          <div className="flex flex-col gap-2" data-testid="admin-products-bulk-attr-result">
            <div className="flex flex-wrap gap-2">
              <GPill tone="success" size="sm">
                {t("products.bulk.setAttrUpdatedCount", { count: result.updated })}
              </GPill>
              {result.failed.length > 0 ? (
                <GPill tone="error" size="sm">
                  {t("products.bulk.setAttrFailedCount", { count: result.failed.length })}
                </GPill>
              ) : null}
            </div>
            {result.failed.length > 0 ? (
              <div className="flex flex-col gap-1 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] p-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                  {t("products.bulk.setAttrFailuresTitle")}
                </span>
                <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto text-xs">
                  {result.failed.map((f) => (
                    <li key={f.product_id} className="flex justify-between gap-2">
                      <span className="font-mono">{f.product_id.slice(0, 8)}</span>
                      <span className="text-[var(--g-error)]">
                        {t(`products.bulk.setAttrFailReason.${f.reason}`, { defaultValue: f.reason })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="admin-products-bulk-attr-submit"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={handleSubmit}
            className="min-h-[44px]"
          >
            {t("products.bulk.setAttrApply", { count: productIds.length })}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default BulkSetAttributeDialog;
